/**
 * Menu Transformer for Glider component in Design System
 *
 * TODO: rwfactoring on types
 */
import { stringEqualsInsensitive } from '@sky-it-livedata-libraries/livedata-commons';
import { IGliderItemProps, IMatchMenu } from "@sky-it-livedata/livedata-sports-design-system";

type GliderItemState = 'enabled' | 'focused' | 'disabled';

type CompetitionRoundStatus = 'past' | 'current' | 'future';

export const GLIDER_ITEM_STATE_ENABLED: GliderItemState = 'enabled';
export const GLIDER_ITEM_STATE_DISABLED: GliderItemState = 'disabled';
export const GLIDER_ITEM_STATE_FOCUSED: GliderItemState = 'focused';

const COMPETITION_ROUND_STATUS_PAST: CompetitionRoundStatus = 'past';
const COMPETITION_ROUND_STATUS_CURRENT: CompetitionRoundStatus = 'current';
const COMPETITION_ROUND_STATUS_FUTURE: CompetitionRoundStatus = 'future';

const HREF_ANCHOR_PREFIX_ID = 'giornata';
const GLIDER_ITEM_PREFIX_LABEL = 'G';

// PAST DISABLED
const mapItemStatePastDisabled = {
  [COMPETITION_ROUND_STATUS_PAST]: GLIDER_ITEM_STATE_DISABLED,
  [COMPETITION_ROUND_STATUS_CURRENT]: GLIDER_ITEM_STATE_ENABLED,
  [COMPETITION_ROUND_STATUS_FUTURE]: GLIDER_ITEM_STATE_ENABLED,
};

// FUTURE DISABLED
const mapItemStateFutureDisabled = {
  [COMPETITION_ROUND_STATUS_PAST]: GLIDER_ITEM_STATE_ENABLED,
  [COMPETITION_ROUND_STATUS_CURRENT]: GLIDER_ITEM_STATE_ENABLED,
  [COMPETITION_ROUND_STATUS_FUTURE]: GLIDER_ITEM_STATE_DISABLED,
};

// ALWAYS ENABLED
const mapItemStateAlwaysEnabled = {
  [COMPETITION_ROUND_STATUS_PAST]: GLIDER_ITEM_STATE_ENABLED,
  [COMPETITION_ROUND_STATUS_CURRENT]: GLIDER_ITEM_STATE_ENABLED,
  [COMPETITION_ROUND_STATUS_FUTURE]: GLIDER_ITEM_STATE_ENABLED,
};

export interface IGliderPayload {
  competitionId: string;
  seasonId: string;
  currentRound: number;
  matchRoundList: Array<ICompetition>;
}
export interface ICompetition {
  competitionPhase?: string | null;
  competitionPhaseRound?: string | null;
  round: number;
  status: CompetitionRoundStatus;
  url?: string | null;
}

const createId = (i: number): string => {
  return `${HREF_ANCHOR_PREFIX_ID}-${i}`;
};

const sortMenuByItemRound = (menuItemA: ICompetition, menuItemB: ICompetition) => {
  if (menuItemA.round > menuItemB.round) {
    return 1;
  } else if (menuItemA.round < menuItemB.round) {
    return -1;
  } else {
    return 0;
  }
};

const getGroupedNonLinears = (menuList: ICompetition[]): Record<string, [ICompetition, ICompetition | undefined]> => {
  const notLinears: ICompetition[] = menuList.filter((element) => element.competitionPhase);

  return notLinears.reduce((grouped, round) => {
    const phase = round.competitionPhase!;
    grouped[phase] = [...(grouped[phase] || []), round];
    return grouped;
  }, {} as { [key: string]: any });
};

const getLastNonLinears = (
  groupedNonLinears: Record<string, [ICompetition, ICompetition | undefined]>
): ICompetition[] => {
  return Object.values(groupedNonLinears).map(([andata, ritorno]) => {
    if (!ritorno) {
      return andata;
    }

    return ritorno;
  });
};

const getCurrentGroupedNonLinears = (params: {
  menuList: ICompetition[];
  currentRound: Number;
}): ICompetition[] | undefined => {
  const { menuList, currentRound } = params;

  const groupedNonLinears = getGroupedNonLinears(menuList);
  const currentGroupedNonLinears = Object.values(groupedNonLinears).find((groupedNonLinear: any) => {
    return groupedNonLinear.find((groupedNonLinearItem: any) => {
      return groupedNonLinearItem.round === currentRound;
    });
  });

  return currentGroupedNonLinears
    ? currentGroupedNonLinears.filter((item: ICompetition | undefined): item is ICompetition => Boolean(item))
    : undefined;
};

const getGliderItemLabel = (menuItem: ICompetition) =>
  menuItem.competitionPhase || `${GLIDER_ITEM_PREFIX_LABEL} ${menuItem.round}`;

/**
 *
 * This function normalize the menuList removing double occurence of a phase
 * based on current status (PAST , CURRENT , FUTURE)
 * @param menuList the Menu List to normalize
 */
export const normalizeMenu = (menuList: ICompetition[]): ICompetition[] => {
  const firstPhaseRound: number | undefined = menuList.find((elm: ICompetition) => elm?.competitionPhase)?.round;
  if (firstPhaseRound !== undefined) {
    const linears: ICompetition[] = menuList.filter((element) => !element.competitionPhase);
    const notLinears: ICompetition[] = menuList.filter((element) => element.competitionPhase);

    const groupedNonLinears = getGroupedNonLinears(notLinears);

    const lastNonLinears: ICompetition[] = getLastNonLinears(groupedNonLinears);

    // merge and order elements
    const menuNormalized: ICompetition[] = [...linears, ...lastNonLinears].sort(sortMenuByItemRound);
    return menuNormalized;
  } else {
    return menuList;
  }
};

/**
 * Get the glider Menu
 * @param menuGlider menu object from GQL
 * @param enableFuture true=enable FUTURE rounds + disable PAST rounds, null=ALL ROUNDS are ENABLED
 */
export const getGlider = (menuGlider: any, enableFuture: boolean | null = false): IMatchMenu => {
  const { currentRound, matchRoundList = [] } = menuGlider;
  console.log('currentRound', currentRound);
  console.log('matchRoundList', matchRoundList);
  return getGliderItems(matchRoundList, enableFuture, currentRound);
};

/**
 * Get the gliderItems Menu
 * @param menuList menu list from GQL
 * @param enableFuture true=enable FUTURE rounds + disable PAST rounds, null=ALL ROUNDS are ENABLED
 * @param currentRound the current round to set active
 */
export const getGliderItems = (
  menuList: ICompetition[],
  enableFuture: boolean | null = false,
  currentRound: number | undefined
): IMatchMenu => {
  const normalizedMenu = normalizeMenu(menuList);

  const gliderItems =
    normalizedMenu &&
    normalizedMenu.map((menuItem: ICompetition) => ({
      href: menuItem.url || `#${createId(menuItem.round)}`,
      label: getGliderItemLabel(menuItem),
      itemState:
        // eslint-disable-next-line no-nested-ternary
        enableFuture === null
          ? mapItemStateAlwaysEnabled[menuItem.status]
          : enableFuture
          ? mapItemStatePastDisabled[menuItem.status]
          : mapItemStateFutureDisabled[menuItem.status],
    }));

  // eslint-disable-next-line functional/no-let
  let activeIndex: number | undefined = currentRound;

  if (currentRound !== undefined && !normalizedMenu.find((menuItem) => menuItem.round === activeIndex)) {
    const currentGroupedNonLinears = getCurrentGroupedNonLinears({ menuList, currentRound });
    activeIndex = currentGroupedNonLinears?.find((element) => element?.round !== currentRound)?.round;
  }

  if (activeIndex === undefined) {
    activeIndex = normalizedMenu?.findIndex((menuItem) => menuItem.status === COMPETITION_ROUND_STATUS_CURRENT);
  }

  return {
    activeIndex: activeIndex || 1,
    gliderItems: gliderItems,
  };
};

/**
 * Get the active index for multiple results in ResultWidget
 * Select the lowest round in Array, then - 1 for index
 * @param getCompetitionResult
 */
export const computeMenuActiveIndexInResults = (
  competitionResults: any[],
  normalizedMenu: Array<IGliderItemProps>
): number => {
  if (competitionResults.length === 0) {
    return 0;
  } else {
    const competitionPhase = competitionResults[0].competitionPhase;
    if (competitionPhase) {
      const index = normalizedMenu.findIndex((elm: any) => stringEqualsInsensitive(elm.label, competitionPhase));
      return index !== -1 ? index : 0;
    }
    return competitionResults[0].round - 1;
  }
};

export interface IGliderItem {
  href?: string;
  itemState?: GliderItemState;
  label: string;
}